import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Loader2, LogOut, RefreshCw, Clock, CheckCircle2, ChefHat, 
  Package, XCircle, CreditCard, Wallet, Users, Trash2, Phone, Mail, ShoppingCart
} from 'lucide-react';
import { toast } from 'sonner';
import { OrderStatus, PaymentMethod, STATUS_LABELS, PAYMENT_METHOD_LABELS } from '@/types/order';
import AdminAddOrder from '@/components/AdminAddOrder';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';

interface OrderItem {
  name_snapshot: string;
  quantity: number;
  gluten_option: 'con_glutine' | 'senza_glutine';
  line_total_cents: number;
}

interface Order {
  id: string;
  customer_name: string;
  customer_email: string;
  customer_phone: string;
  pickup_time: string | null;
  order_note: string | null;
  status: OrderStatus;
  payment_method: PaymentMethod;
  cancel_reason?: string | null;
  is_paid: boolean;
  paid_at: string | null;
  total_cents: number;
  created_at: string;
  order_items: OrderItem[];
}

// Workflow status (separate from payment status)
type WorkflowStatus = 'ricevuto' | 'in_preparazione' | 'pronto' | 'annullato';

const getWorkflowStatus = (order: Order): WorkflowStatus => {
  if (order.status === 'annullato') return 'annullato';
  if (order.status === 'pronto') return 'pronto';
  if (order.status === 'in_preparazione') return 'in_preparazione';
  return 'ricevuto'; // da_pagare, pagato → just received
};

const WORKFLOW_LABELS: Record<WorkflowStatus, string> = {
  'ricevuto': 'Ricevuto',
  'in_preparazione': 'In preparazione',
  'pronto': 'Pronto',
  'annullato': 'Annullato',
};

const WORKFLOW_ICONS: Record<WorkflowStatus, React.ReactNode> = {
  'ricevuto': <Clock className="h-4 w-4" />,
  'in_preparazione': <ChefHat className="h-4 w-4" />,
  'pronto': <Package className="h-4 w-4" />,
  'annullato': <XCircle className="h-4 w-4" />,
};

const WORKFLOW_COLORS: Record<WorkflowStatus, string> = {
  'ricevuto': 'bg-blue-500/10 text-blue-600 border-blue-500/30',
  'in_preparazione': 'bg-orange-500/10 text-orange-600 border-orange-500/30',
  'pronto': 'bg-green-500/10 text-green-600 border-green-500/30',
  'annullato': 'bg-red-500/10 text-red-600 border-red-500/30',
};

// Legacy status maps (kept for compatibility)
const STATUS_ICONS: Record<OrderStatus, React.ReactNode> = {
  'da_pagare': <Clock className="h-4 w-4" />,
  'pagato': <CheckCircle2 className="h-4 w-4" />,
  'in_preparazione': <ChefHat className="h-4 w-4" />,
  'pronto': <Package className="h-4 w-4" />,
  'annullato': <XCircle className="h-4 w-4" />,
};

const STATUS_COLORS: Record<OrderStatus, string> = {
  'da_pagare': 'bg-yellow-500/10 text-yellow-600 border-yellow-500/30',
  'pagato': 'bg-blue-500/10 text-blue-600 border-blue-500/30',
  'in_preparazione': 'bg-orange-500/10 text-orange-600 border-orange-500/30',
  'pronto': 'bg-green-500/10 text-green-600 border-green-500/30',
  'annullato': 'bg-red-500/10 text-red-600 border-red-500/30',
};

// Play notification sound using Web Audio API
const playNotificationSound = () => {
  try {
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    
    // Resume context if suspended (browser autoplay policy)
    if (audioContext.state === 'suspended') {
      audioContext.resume();
    }
    
    // Create a pleasant notification sound
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    oscillator.frequency.setValueAtTime(880, audioContext.currentTime); // A5
    oscillator.frequency.setValueAtTime(1108.73, audioContext.currentTime + 0.1); // C#6
    oscillator.frequency.setValueAtTime(1318.51, audioContext.currentTime + 0.2); // E6
    
    oscillator.type = 'sine';
    
    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
    
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.5);
  } catch (err) {
    console.log('Could not play notification sound:', err);
  }
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<'all' | 'ricevuto' | 'in_preparazione' | 'pronto' | 'annullato'>('all');
  const [paymentFilter, setPaymentFilter] = useState<'all' | 'paid' | 'unpaid'>('all');
  const [updatingOrder, setUpdatingOrder] = useState<string | null>(null);
  const [ordersEnabled, setOrdersEnabled] = useState(true);
  const [togglingOrders, setTogglingOrders] = useState(false);
  const [maxOrdersPerSlot, setMaxOrdersPerSlot] = useState<number>(5);
  const [updatingMaxOrders, setUpdatingMaxOrders] = useState(false);
  const lastOrderCountRef = useRef<number | null>(null);

  const fetchSettings = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('app_settings')
        .select('orders_enabled, max_orders_per_slot')
        .eq('id', 'main')
        .maybeSingle();
      
      if (error) throw error;
      if (data) {
        setOrdersEnabled(data.orders_enabled);
        setMaxOrdersPerSlot(data.max_orders_per_slot ?? 5);
      }
    } catch (err) {
      console.error('Error fetching settings:', err);
    }
  }, []);

  const toggleOrdersEnabled = async () => {
    setTogglingOrders(true);
    try {
      const newValue = !ordersEnabled;
      const { error } = await supabase
        .from('app_settings')
        .update({ orders_enabled: newValue, updated_at: new Date().toISOString() })
        .eq('id', 'main');

      if (error) throw error;
      
      setOrdersEnabled(newValue);
      toast.success(newValue ? 'Ordini online attivati' : 'Ordini online disattivati');
    } catch (err) {
      console.error('Error toggling orders:', err);
      toast.error('Errore nell\'aggiornamento');
    } finally {
      setTogglingOrders(false);
    }
  };

  const updateMaxOrdersPerSlot = async (newValue: number) => {
    if (newValue < 1 || newValue > 50) return;
    setUpdatingMaxOrders(true);
    try {
      const { error } = await supabase
        .from('app_settings')
        .update({ max_orders_per_slot: newValue, updated_at: new Date().toISOString() })
        .eq('id', 'main');

      if (error) throw error;
      
      setMaxOrdersPerSlot(newValue);
      toast.success(`Limite ordini per fascia: ${newValue}`);
    } catch (err) {
      console.error('Error updating max orders:', err);
      toast.error('Errore nell\'aggiornamento');
    } finally {
      setUpdatingMaxOrders(false);
    }
  };

  const fetchOrders = useCallback(async () => {
    try {
      let query = supabase
        .from('orders')
        .select(`
          id,
          customer_name,
          customer_email,
          customer_phone,
          pickup_time,
          order_note,
          status,
          payment_method,
          is_paid,
          paid_at,
          total_cents,
          created_at,
          order_items (
            name_snapshot,
            quantity,
            gluten_option,
            line_total_cents
          )
        `)
        .order('pickup_time', { ascending: true, nullsFirst: false });

      // Status filter - 'ricevuto' maps to da_pagare OR pagato (just received, not in preparation yet)
      if (statusFilter === 'ricevuto') {
        query = query.in('status', ['da_pagare', 'pagato']);
      } else if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }

      // Payment filter
      if (paymentFilter === 'paid') {
        query = query.eq('is_paid', true);
      } else if (paymentFilter === 'unpaid') {
        query = query.eq('is_paid', false);
      }

      const { data, error } = await query;

      if (error) throw error;
      setOrders(data || []);
    } catch (err: any) {
      console.error('Error fetching orders:', err);
      toast.error('Errore nel caricamento ordini');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, paymentFilter]);

  useEffect(() => {
    // Check auth
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate('/admin/login');
        return;
      }

      const { data: roles } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', session.user.id)
        .eq('role', 'admin')
        .single();

      if (!roles) {
        toast.error('Accesso non autorizzato');
        await supabase.auth.signOut();
        navigate('/admin/login');
        return;
      }

      fetchOrders();
      fetchSettings();
    };

    checkAuth();

    // Subscribe to auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_OUT') {
        navigate('/admin/login');
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate, fetchOrders, fetchSettings]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(fetchOrders, 30000);
    return () => clearInterval(interval);
  }, [fetchOrders]);

  // Realtime subscription for new orders
  useEffect(() => {
    const channel = supabase
      .channel('orders-changes')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'orders',
        },
        (payload) => {
          console.log('New order received:', payload);
          playNotificationSound();
          toast.success('🍕 Nuovo ordine ricevuto!');
          fetchOrders();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchOrders]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/admin/login');
  };

  const updateOrderStatus = async (orderId: string, newStatus: OrderStatus) => {
    setUpdatingOrder(orderId);
    try {
      const { error } = await supabase
        .from('orders')
        .update({ status: newStatus })
        .eq('id', orderId);

      if (error) throw error;
      toast.success(`Stato aggiornato: ${STATUS_LABELS[newStatus]}`);
      fetchOrders();
    } catch (err: any) {
      toast.error('Errore nell\'aggiornamento');
    } finally {
      setUpdatingOrder(null);
    }
  };

  const markAsPaid = async (orderId: string, currentStatus: OrderStatus) => {
    setUpdatingOrder(orderId);
    try {
      const updates: { is_paid: boolean; paid_at: string; status?: OrderStatus } = {
        is_paid: true,
        paid_at: new Date().toISOString(),
      };
      
      // Only change status to 'pagato' if currently 'da_pagare'
      if (currentStatus === 'da_pagare') {
        updates.status = 'pagato';
      }
      
      const { error } = await supabase
        .from('orders')
        .update(updates)
        .eq('id', orderId);

      if (error) throw error;
      toast.success('Ordine segnato come pagato');
      fetchOrders();
    } catch (err: any) {
      toast.error('Errore nell\'aggiornamento');
    } finally {
      setUpdatingOrder(null);
    }
  };

  const cancelOrder = async (orderId: string) => {
    const reason = window.prompt('Motivo annullamento (opzionale):');
    if (reason === null) return; // User cancelled prompt

    setUpdatingOrder(orderId);
    try {
      const { error } = await supabase
        .from('orders')
        .update({
          status: 'annullato',
          cancel_reason: reason || 'Annullato dalla pizzeria',
        })
        .eq('id', orderId);

      if (error) throw error;
      toast.success('Ordine annullato');
      fetchOrders();
    } catch (err: any) {
      toast.error('Errore nell\'annullamento');
    } finally {
    setUpdatingOrder(null);
    }
  };

  const resetOrderHistory = async () => {
    const confirmed = window.confirm('⚠️ Sei sicuro di voler eliminare TUTTI gli ordini? Questa azione è irreversibile.');
    if (!confirmed) return;

    const doubleConfirm = window.confirm('Conferma definitiva: Tutti gli ordini verranno eliminati permanentemente.');
    if (!doubleConfirm) return;

    setLoading(true);
    try {
      // First delete order items (due to foreign key)
      const { error: itemsError } = await supabase
        .from('order_items')
        .delete()
        .neq('id', '00000000-0000-0000-0000-000000000000'); // Delete all

      if (itemsError) throw itemsError;

      // Then delete orders
      const { error: ordersError } = await supabase
        .from('orders')
        .delete()
        .neq('id', '00000000-0000-0000-0000-000000000000'); // Delete all

      if (ordersError) throw ordersError;

      toast.success('Storico ordini resettato');
      setOrders([]);
    } catch (err: any) {
      console.error('Error resetting orders:', err);
      toast.error('Errore nel reset degli ordini');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (isoString: string | null) => {
    if (!isoString) return '-';
    return new Date(isoString).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDateTime = (isoString: string) => {
    return new Date(isoString).toLocaleString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const isCompletedOrder = (o: Order) => o.status === 'annullato' || (o.status === 'pronto' && o.is_paid);
  const activeOrdersCount = orders.filter(o => !isCompletedOrder(o)).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-card border-b border-border">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-lg sm:text-xl font-bold text-foreground">🍕 Dashboard Ordini</h1>
              <p className="text-xs sm:text-sm text-muted-foreground">{activeOrdersCount} ordini attivi</p>
            </div>
            <div className="flex items-center gap-1 sm:gap-2">
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0 sm:h-9 sm:w-auto sm:px-3" onClick={fetchOrders}>
                <RefreshCw className="h-4 w-4 sm:mr-1" />
                <span className="hidden sm:inline">Aggiorna</span>
              </Button>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0 sm:h-9 sm:w-auto sm:px-3" onClick={handleLogout}>
                <LogOut className="h-4 w-4 sm:mr-1" />
                <span className="hidden sm:inline">Esci</span>
              </Button>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 mt-2">
            <AdminAddOrder onOrderCreated={fetchOrders} />
            <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => navigate('/admin/cucina')}>
              <ChefHat className="h-3.5 w-3.5 mr-1" />
              Forno
            </Button>
            <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => navigate('/admin/users')}>
              <Users className="h-3.5 w-3.5 mr-1" />
              Admin
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              className="h-8 text-xs text-destructive border-destructive/30 hover:bg-destructive/10"
              onClick={resetOrderHistory}
            >
              <Trash2 className="h-3.5 w-3.5 mr-1" />
              Reset
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-4 sm:py-6">
        {/* Orders Toggle + Filters */}
        <div className="mb-4 sm:mb-6 grid grid-cols-2 sm:flex sm:flex-wrap items-center gap-3 sm:gap-4">
          {/* Orders Online Toggle */}
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-border bg-card col-span-2 sm:col-span-1">
            <ShoppingCart className={`h-4 w-4 flex-shrink-0 ${ordersEnabled ? 'text-green-500' : 'text-muted-foreground'}`} />
            <Label htmlFor="orders-toggle" className="text-xs sm:text-sm font-medium cursor-pointer whitespace-nowrap">
              Ordini Online
            </Label>
            <Switch
              id="orders-toggle"
              checked={ordersEnabled}
              onCheckedChange={toggleOrdersEnabled}
              disabled={togglingOrders}
            />
            <span className={`text-xs font-medium px-2 py-0.5 rounded ${ordersEnabled ? 'bg-green-500/10 text-green-600' : 'bg-red-500/10 text-red-600'}`}>
              {ordersEnabled ? 'Attivi' : 'Off'}
            </span>
          </div>

          {/* Max Orders Per Slot */}
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-border bg-card col-span-2 sm:col-span-1">
            <Clock className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            <Label className="text-xs sm:text-sm font-medium whitespace-nowrap">
              Max/fascia
            </Label>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                className="h-7 w-7 p-0"
                onClick={() => updateMaxOrdersPerSlot(maxOrdersPerSlot - 1)}
                disabled={updatingMaxOrders || maxOrdersPerSlot <= 1}
              >
                -
              </Button>
              <span className="w-8 text-center font-semibold text-foreground">{maxOrdersPerSlot}</span>
              <Button
                variant="outline"
                size="sm"
                className="h-7 w-7 p-0"
                onClick={() => updateMaxOrdersPerSlot(maxOrdersPerSlot + 1)}
                disabled={updatingMaxOrders || maxOrdersPerSlot >= 50}
              >
                +
              </Button>
            </div>
          </div>

          {/* Status Filter */}
          <div className="flex items-center gap-2">
            <span className="text-xs sm:text-sm text-muted-foreground">Stato:</span>
            <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as 'all' | 'ricevuto' | 'in_preparazione' | 'pronto' | 'annullato')}>
              <SelectTrigger className="w-[120px] sm:w-40 h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti</SelectItem>
                <SelectItem value="ricevuto">Ricevuto</SelectItem>
                <SelectItem value="in_preparazione">In preparazione</SelectItem>
                <SelectItem value="pronto">Pronto</SelectItem>
                <SelectItem value="annullato">Annullato</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Payment Filter */}
          <div className="flex items-center gap-2">
            <span className="text-xs sm:text-sm text-muted-foreground">Pag.:</span>
            <Select value={paymentFilter} onValueChange={(v) => setPaymentFilter(v as 'all' | 'paid' | 'unpaid')}>
              <SelectTrigger className="w-[110px] sm:w-36 h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti</SelectItem>
                <SelectItem value="paid">Pagato</SelectItem>
                <SelectItem value="unpaid">Da pagare</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Active Orders */}
        {orders.filter(o => !isCompletedOrder(o)).length === 0 && orders.filter(o => isCompletedOrder(o)).length === 0 ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">Nessun ordine trovato</p>
          </div>
        ) : (
          <>
            {/* Active Orders Section */}
            {orders.filter(o => !isCompletedOrder(o)).length > 0 && (
              <>
                <h2 className="text-lg font-semibold text-foreground mb-4">Ordini Attivi</h2>
                <div className="grid gap-3 sm:gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 mb-8">
                  {orders.filter(o => !isCompletedOrder(o)).map((order) => (
                    <div key={order.id} className="card-elevated p-4 space-y-4">
                      {/* Header */}
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-semibold text-foreground">{order.customer_name}</p>
                          <p className="text-xs text-muted-foreground">#{order.id.slice(-8).toUpperCase()}</p>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium border flex items-center gap-1 ${WORKFLOW_COLORS[getWorkflowStatus(order)]}`}>
                          {WORKFLOW_ICONS[getWorkflowStatus(order)]}
                          {WORKFLOW_LABELS[getWorkflowStatus(order)]}
                        </span>
                      </div>

                      {/* Contact */}
                      <div className="flex flex-col sm:flex-row gap-1 sm:gap-4 text-sm">
                        <span className="flex items-center gap-1 text-muted-foreground">
                          <Phone className="h-3 w-3 flex-shrink-0" />
                          {order.customer_phone}
                        </span>
                        <span className="flex items-center gap-1 text-muted-foreground min-w-0">
                          <Mail className="h-3 w-3 flex-shrink-0" />
                          <span className="truncate">{order.customer_email === 'ordine.manuale@pizzeria.it' ? '-' : order.customer_email}</span>
                        </span>
                      </div>

                      {/* Items */}
                      <div className="bg-muted/50 rounded-lg p-3 space-y-1">
                        {order.order_items.map((item, idx) => (
                          <div key={idx} className="flex justify-between text-sm">
                            <span>
                              {item.quantity}x {item.name_snapshot}
                              {item.gluten_option === 'senza_glutine' && (
                                <span className="text-green-600 text-xs ml-1">(GF)</span>
                              )}
                            </span>
                            <span className="text-muted-foreground">€{(item.line_total_cents / 100).toFixed(2)}</span>
                          </div>
                        ))}
                        <div className="border-t border-border pt-1 mt-2 flex justify-between font-semibold">
                          <span>Totale</span>
                          <span className="text-primary">€{(order.total_cents / 100).toFixed(2)}</span>
                        </div>
                      </div>

                      {/* Info */}
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <p className="text-muted-foreground">Ritiro</p>
                          <p className="font-medium">{formatTime(order.pickup_time)}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Pagamento</p>
                          <p className="font-medium flex items-center gap-1">
                            {order.is_paid ? (
                              <>
                                <CheckCircle2 className="h-3 w-3 text-green-500" />
                                <span className="text-green-600">Pagato</span>
                              </>
                            ) : (
                              <>
                                <Clock className="h-3 w-3 text-yellow-500" />
                                <span className="text-yellow-600">
                                  Da pagare ({order.payment_method === 'stripe' ? 'online' : 'ritiro'})
                                </span>
                              </>
                            )}
                          </p>
                        </div>
                        <div className="col-span-2">
                          <p className="text-muted-foreground">Ricevuto</p>
                          <p className="font-medium">{formatDateTime(order.created_at)}</p>
                        </div>
                      </div>

                      {order.order_note && (
                        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-2">
                          <p className="text-sm text-yellow-700">📝 {order.order_note}</p>
                        </div>
                      )}

                      {/* Actions */}
                      <div className="flex flex-wrap gap-2 pt-2 border-t border-border">
                        {!order.is_paid && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="flex-1 h-8 text-xs"
                            onClick={() => markAsPaid(order.id, order.status)}
                            disabled={updatingOrder === order.id}
                          >
                            {updatingOrder === order.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3 mr-1" />}
                            Segna Pagato
                          </Button>
                        )}

                        {(order.status === 'da_pagare' || order.status === 'pagato') && (
                          <Button
                            size="sm"
                            variant="default"
                            className="flex-1 h-8 text-xs"
                            onClick={() => updateOrderStatus(order.id, 'in_preparazione')}
                            disabled={updatingOrder === order.id}
                          >
                            {updatingOrder === order.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <ChefHat className="h-3 w-3 mr-1" />}
                            In Preparazione
                          </Button>
                        )}

                        {order.status === 'in_preparazione' && (
                          <Button
                            size="sm"
                            variant="default"
                            className="flex-1 h-8 text-xs bg-green-600 hover:bg-green-700"
                            onClick={() => updateOrderStatus(order.id, 'pronto')}
                            disabled={updatingOrder === order.id}
                          >
                            {updatingOrder === order.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Package className="h-3 w-3 mr-1" />}
                            Pronto
                          </Button>
                        )}

                        <Button
                          size="sm"
                          variant="outline"
                          className="h-8 text-xs text-destructive border-destructive/30 hover:bg-destructive/10"
                          onClick={() => cancelOrder(order.id)}
                          disabled={updatingOrder === order.id}
                        >
                          <XCircle className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* Completed/Cancelled Orders Section */}
            {orders.filter(o => isCompletedOrder(o)).length > 0 && (
              <>
                <h2 className="text-lg font-semibold text-muted-foreground mb-4 border-t border-border pt-6">
                  Ordini Completati / Annullati
                </h2>
                <div className="grid gap-3 sm:gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 opacity-70">
                  {orders.filter(o => isCompletedOrder(o)).map((order) => (
                    <div key={order.id} className="card-elevated p-4 space-y-4">
                      {/* Header */}
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-semibold text-foreground">{order.customer_name}</p>
                          <p className="text-xs text-muted-foreground">#{order.id.slice(-8).toUpperCase()}</p>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium border flex items-center gap-1 ${WORKFLOW_COLORS[getWorkflowStatus(order)]}`}>
                          {WORKFLOW_ICONS[getWorkflowStatus(order)]}
                          {WORKFLOW_LABELS[getWorkflowStatus(order)]}
                        </span>
                      </div>

                      {/* Contact */}
                      <div className="flex flex-col sm:flex-row gap-1 sm:gap-4 text-sm">
                        <span className="flex items-center gap-1 text-muted-foreground">
                          <Phone className="h-3 w-3 flex-shrink-0" />
                          {order.customer_phone}
                        </span>
                        <span className="flex items-center gap-1 text-muted-foreground min-w-0">
                          <Mail className="h-3 w-3 flex-shrink-0" />
                          <span className="truncate">{order.customer_email === 'ordine.manuale@pizzeria.it' ? '-' : order.customer_email}</span>
                        </span>
                      </div>

                      {/* Items */}
                      <div className="bg-muted/50 rounded-lg p-3 space-y-1">
                        {order.order_items.map((item, idx) => (
                          <div key={idx} className="flex justify-between text-sm">
                            <span>
                              {item.quantity}x {item.name_snapshot}
                              {item.gluten_option === 'senza_glutine' && (
                                <span className="text-green-600 text-xs ml-1">(GF)</span>
                              )}
                            </span>
                            <span className="text-muted-foreground">€{(item.line_total_cents / 100).toFixed(2)}</span>
                          </div>
                        ))}
                        <div className="border-t border-border pt-1 mt-2 flex justify-between font-semibold">
                          <span>Totale</span>
                          <span className="text-primary">€{(order.total_cents / 100).toFixed(2)}</span>
                        </div>
                      </div>

                      {/* Info */}
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <p className="text-muted-foreground">Ritiro</p>
                          <p className="font-medium">{formatTime(order.pickup_time)}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Pagamento</p>
                          <p className="font-medium flex items-center gap-1">
                            {order.is_paid ? (
                              <>
                                <CheckCircle2 className="h-3 w-3 text-green-500" />
                                <span className="text-green-600">Pagato</span>
                              </>
                            ) : (
                              <>
                                <Clock className="h-3 w-3 text-yellow-500" />
                                <span className="text-yellow-600">
                                  Da pagare ({order.payment_method === 'stripe' ? 'online' : 'ritiro'})
                                </span>
                              </>
                            )}
                          </p>
                        </div>
                        <div className="col-span-2">
                          <p className="text-muted-foreground">Ricevuto</p>
                          <p className="font-medium">{formatDateTime(order.created_at)}</p>
                        </div>
                      </div>

                      {order.order_note && (
                        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-2">
                          <p className="text-sm text-yellow-700">📝 {order.order_note}</p>
                        </div>
                      )}

                      {order.status === 'pronto' && !order.is_paid && (
                        <div className="flex gap-2 pt-2 border-t border-border">
                          <Button
                            size="sm"
                            variant="outline"
                            className="flex-1 h-8 text-xs"
                            onClick={() => markAsPaid(order.id, order.status)}
                            disabled={updatingOrder === order.id}
                          >
                            {updatingOrder === order.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3 mr-1" />}
                            Segna Pagato
                          </Button>
                        </div>
                      )}

                      {order.status === 'annullato' && order.cancel_reason && (
                        <p className="text-xs text-muted-foreground">
                          Motivo: {order.cancel_reason}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;
